import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { useT } from "@/lib/i18n";

const TEXTURE =
  "https://images.unsplash.com/photo-1602475063211-3d98d60e3b1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbm9pc2UlMjB0ZXh0dXJlJTIwZ3JhaW58ZW58MHx8fHwxNzgxMDQ3NjAzfDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  show: (i = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 + i * 0.08 },
  }),
};

export const Hero = () => {
  const { t } = useT();
  const h = t.hero;
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative min-h-screen w-full overflow-hidden border-b border-white/10"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.22] mix-blend-screen"
        style={{
          backgroundImage: `url(${TEXTURE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div aria-hidden className="absolute inset-0 scan-lines opacity-40 pointer-events-none" />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 30%, rgba(255,59,0,0.18) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(255,255,255,0.04) 0%, transparent 60%)",
        }}
      />

      <motion.div
        initial="hidden"
        animate="show"
        custom={0}
        variants={fadeUp}
        className="absolute top-24 md:top-28 left-6 md:left-12 lg:left-24 right-6 md:right-12 lg:right-24 flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/40"
      >
        <span>{h.meta1}</span>
        <span className="hidden md:inline">{h.meta2}</span>
        <span>
          {h.statusLabel}&nbsp;<span className="text-[#ff3b00]">{h.statusValue}</span>
          <span className="blink text-[#ff3b00]">_</span>
        </span>
      </motion.div>

      <div className="relative z-10 flex flex-col justify-end min-h-screen px-6 md:px-12 lg:px-24 pb-16 md:pb-20 pt-40">
        <motion.h1
          data-testid={HERO.title}
          initial="hidden"
          animate="show"
          custom={1}
          variants={fadeUp}
          className="font-display uppercase text-[22vw] md:text-[18vw] lg:text-[15vw] leading-[0.82] tracking-tighter text-white"
        >
          BODE<span className="text-[#ff3b00]">G</span>A
        </motion.h1>

        <div className="mt-12 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <motion.div
            initial="hidden"
            animate="show"
            custom={2}
            variants={fadeUp}
            className="md:col-span-5 lg:col-span-4"
          >
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#ff3b00] mb-3">
              {h.mandate}
            </div>
            <p
              data-testid={HERO.subtitle}
              className="font-display uppercase text-2xl md:text-3xl lg:text-4xl leading-[0.95] tracking-tight text-white text-balance"
            >
              {h.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            custom={3}
            variants={fadeUp}
            className="md:col-span-5 lg:col-span-5 md:col-start-7 lg:col-start-7"
          >
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">
              {h.dossierLabel}
            </div>
            <p
              data-testid={HERO.bio}
              className="font-mono text-sm md:text-base leading-relaxed text-white/70 max-w-xl text-pretty"
            >
              {h.bioLead}&nbsp;<span className="text-white">{h.bioYears}</span>&nbsp;{h.bioMid}&nbsp;
              <span className="text-[#ff3b00]">{h.bioAccent}</span>
            </p>

            <motion.a
              href="#contact"
              data-testid={HERO.cta}
              whileHover={{ x: 4 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="mt-8 inline-flex items-center gap-3 group"
            >
              <span className="inline-flex items-center gap-3 px-6 py-4 bg-[#ff3b00] text-black font-mono text-xs md:text-sm tracking-[0.25em] uppercase hover:bg-white transition-colors duration-200">
                {h.cta}
                <ArrowDownRight className="w-4 h-4" strokeWidth={2.5} />
              </span>
            </motion.a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-14 md:mt-20 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] md:text-xs tracking-[0.25em] uppercase text-white/40"
        >
          <span data-testid={HERO.scrollIndicator}>{h.scroll}</span>
          <span className="hidden md:inline">{h.methods}</span>
          <span>{h.copyright}</span>
        </motion.div>
      </div>
    </section>
  );
};
